import { useAtom, useAtomValue } from 'jotai';
import { Flower2, Leaf, Moon, Snowflake, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  goodWeatherDaylightAtom,
  goodWeatherSeasonAtom,
  roomModeSettingsAtom,
  settingsChangedAtom,
} from '../../atoms/settings';
import {
  GOOD_WEATHER_DAYLIGHT_OPTIONS,
  GOOD_WEATHER_SEASON_OPTIONS,
  ROOM_MODE_OPTIONS,
} from '../../constants/room-mode';

const DAYLIGHT_ICONS = {
  day: Sun,
  night: Moon,
} as const;

const SEASON_ICONS = {
  winter: Snowflake,
  spring: Flower2,
  summer: Sun,
  autumn: Leaf,
} as const;

export const GoodWeatherOptionsSettings = () => {
  const roomMode = useAtomValue(roomModeSettingsAtom);
  const [daylight, setDaylight] = useAtom(goodWeatherDaylightAtom);
  const [season, setSeason] = useAtom(goodWeatherSeasonAtom);
  const [, setSettingsChanged] = useAtom(settingsChangedAtom);
  const { t } = useTranslation();

  const handleDaylight = (value: typeof daylight) => {
    setDaylight(daylight === value ? undefined : value);
    setSettingsChanged(true);
  };
  const handleSeason = (value: typeof season) => {
    setSeason(season === value ? undefined : value);
    setSettingsChanged(true);
  };

  if (roomMode !== ROOM_MODE_OPTIONS.GOOD_WEATHER.value) {
    return null;
  }

  return (
    <div className="flex flex-row flex-wrap items-end gap-3">
      {/* Daylight: Day & Night – 2 icons */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-text-primary">{t('goodWeatherDaylight')}</span>
        <div className="flex gap-1.5">
          {GOOD_WEATHER_DAYLIGHT_OPTIONS.map((opt) => {
            const Icon = DAYLIGHT_ICONS[opt.value];
            const isSelected = daylight === opt.value;
            return (
              <Tooltip key={opt.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t(
                      `goodWeather${opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}`
                    )}
                    aria-pressed={isSelected}
                    onClick={() => handleDaylight(opt.value)}
                    className={cn(
                      'flex size-9 items-center justify-center rounded-sm border transition-colors',
                      isSelected
                        ? 'border-primary-light bg-primary-light/15 text-primary-light'
                        : 'border-[#EFF4F5] bg-[#EFF4F5] text-text-secondary hover:border-primary-light/50 hover:bg-primary-light/10'
                    )}
                  >
                    <Icon className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-56 text-balance rounded border bg-white p-2 text-sm"
                >
                  {t(opt.descriptionKey)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div className="mb-1 h-7 w-px bg-border" />

      {/* Season – 4 icons */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-text-primary">{t('goodWeatherSeason')}</span>
        <div className="flex flex-wrap gap-1.5">
          {GOOD_WEATHER_SEASON_OPTIONS.map((opt) => {
            const Icon = SEASON_ICONS[opt.value];
            const isSelected = season === opt.value;
            return (
              <Tooltip key={opt.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t(
                      `goodWeather${opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}`
                    )}
                    aria-pressed={isSelected}
                    onClick={() => handleSeason(opt.value)}
                    className={cn(
                      'flex size-9 items-center justify-center rounded-sm border transition-colors',
                      isSelected
                        ? 'border-primary-light bg-primary-light/15 text-primary-light'
                        : 'border-[#EFF4F5] bg-[#EFF4F5] text-text-secondary hover:border-primary-light/50 hover:bg-primary-light/10'
                    )}
                  >
                    <Icon className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-56 text-balance rounded border bg-white p-2 text-sm"
                >
                  {t(opt.descriptionKey)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
};
